import React, { useState, useEffect } from "react";
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { BASEURL } from "../Config";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { Link, useHistory, NavLink } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import swal from "sweetalert";
import { getElpChapterInfo } from '../modules/actions';
import { getClassInfo } from "../modules/reducers";

const ElpChapters = () => {
    const [offset, setOffset] = useState(0);
    const [slice, setSlice] = useState([]);
    const [perPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [editableRow, setEditableRow] = useState(null);
    const [cname, setCname] = useState("");
    const [id, setId] = useState("");
    const [pageCount, setPageCount] = useState(0);
    const [chapters, setChapters] = useState([]);
    // Rearrange mode keeps its own copy of the list so dragging can be
    // cancelled without touching what is saved.
    const [isRearranging, setIsRearranging] = useState(false);
    const [rearranged, setRearranged] = useState([]);
    const [dragIndex, setDragIndex] = useState(null);
    const [savingOrder, setSavingOrder] = useState(false);

    const history = useHistory();
    const dispatch = useDispatch();
    const book = useSelector(getClassInfo);

    const getClassUuid = () => (history.location.state ? history.location.state.bid : book.uuid);

    const handlePageClick = (e) => {
        const selectedPage = e.selected;
        const offset = selectedPage * perPage;
        setCurrentPage(selectedPage);
        setOffset(offset);
    };

    const receivedData = () => {
        axios.get(`${BASEURL}/api/v1/elp/chapters/fetch-all-by-class/${getClassUuid()}`)
            .then(res => {
                const data = res.data.filter(item => !item.isDeleted);
                const slice = data.slice(offset, offset + perPage);
                setChapters(data);
                setSlice(slice);
                setPageCount(Math.ceil(data.length / perPage));
            });
    };

    useEffect(() => {
        receivedData();
    }, [offset, currentPage]);

    const handleUpdateChapter = () => {
        const data = { name: cname };
        axios.put(`${BASEURL}/api/v1/elp/chapters/update/${id}`, data)
            .then(res => {
                if (res.status === 200) {
                    receivedData();
                    setEditableRow(null);
                }
            })
            .catch(err => {
                console.error('Update chapter Error', err);
            });
    };


    // ---- Rearrange ---------------------------------------------------------
    // The whole class is shown while rearranging (pagination off), otherwise a
    // chapter could never be moved from page 2 to page 1.

    const handleStartRearrange = () => {
        setRearranged([...chapters]);
        setEditableRow(null);
        setDragIndex(null);
        setIsRearranging(true);
    };

    const handleCancelRearrange = () => {
        setIsRearranging(false);
        setRearranged([]);
        setDragIndex(null);
    };

    const moveChapter = (from, to) => {
        if (from === to || to < 0 || to >= rearranged.length) {
            return;
        }
        const next = [...rearranged];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setRearranged(next);
    };

    const handleDrop = (index) => {
        if (dragIndex === null) {
            return;
        }
        moveChapter(dragIndex, index);
        setDragIndex(null);
    };

    const handleSaveOrder = () => {
        const chapterUuids = rearranged.map((chapter) => chapter.uuid);
        setSavingOrder(true);
        axios.put(`${BASEURL}/api/v1/elp/chapters/reorder`, { class_uuid: getClassUuid(), chapterUuids })
            .then(res => {
                setSavingOrder(false);
                setIsRearranging(false);
                setRearranged([]);
                setDragIndex(null);
                receivedData();
                swal('Success', 'Chapter order saved', 'success');
            })
            .catch(err => {
                console.error('Reorder chapters Error', err);
                setSavingOrder(false);
                // The ELP endpoints send the failure reason as a plain string.
                const data = err.response ? err.response.data : null;
                const message = typeof data === 'string' && data
                    ? data
                    : (data && data.message ? data.message : 'Could not save the new order');
                swal('Error', message, 'error');
            });
    };

    const handleOnViewTopics = (cid) => {
        //console.log('cid', cid)
        dispatch(getElpChapterInfo(cid));
        history.push({
            pathname: '/elpQuestions',
            state: { cid: cid }
        });
    };

    const handleOnDeleteChapter = (id) => {
        swal({
            title: "Are you sure?",
            text: "All the questions and topics related to this chapter will be deleted!",
            icon: "warning",
            buttons: ['No', 'Yes'],
            dangerMode: true,
        }).then(isConfirm => {
            if (isConfirm) {
                axios.delete(`${BASEURL}/api/v1/elp/chapters/delete/${id}`)
                    .then(res => {
                        if (res.status === 200) {
                            swal('Success', 'Chapter Deleted Successfully', 'success').then(() => {
                                receivedData();
                            });
                        } else {
                            swal('Error', 'Error in deleting the chapter', 'error').then(() => {
                                receivedData();
                            });
                        }
                    });
            }
        });
    };

    return (
        <div id="wrapper">
            <Sidebar act="classes" />
            <div className="content-page">
                <div className="content">
                    <Topbar act="ElpChapters" breadcrumbItems={["ELP", { label: 'Classes', path: '/classes' }, "View Chapters"]} />
                    <div className="container-fluid main-container">
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body main-card">
                                        <h4 className="page-title">Class name: {book.name}</h4>
                                        <Link className="btn btn-primary btn-sm mr-2" to="/addElpChapters">
                                            <i className="mdi mdi-plus-circle" /> Add Chapters
                                        </Link>
                                        {isRearranging ? (
                                            <>
                                                <button
                                                    className="btn btn-success btn-sm mr-2"
                                                    disabled={savingOrder}
                                                    onClick={handleSaveOrder}
                                                >
                                                    <i className="mdi mdi-content-save-outline" /> {savingOrder ? 'Saving...' : 'Save Order'}
                                                </button>
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    disabled={savingOrder}
                                                    onClick={handleCancelRearrange}
                                                >
                                                    Cancel
                                                </button>
                                            </>
                                        ) : (
                                            <button
                                                className="btn btn-info btn-sm"
                                                disabled={chapters.length < 2}
                                                onClick={handleStartRearrange}
                                            >
                                                <i className="mdi mdi-sort" /> Rearrange
                                            </button>
                                        )}
                                        {isRearranging ? (
                                            <p className="text-muted mt-2 mb-0">
                                                Drag a chapter to a new position, or use the arrows, then click Save Order.
                                            </p>
                                        ) : null}
                                        <table className="table table-centered mb-0 mt-2">
                                            <thead>
                                                <tr>
                                                    <th>Name of the Chapter</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(isRearranging ? rearranged : slice).length === 0 ? (
                                                    <tr className="text-center">
                                                        <td colSpan="2">No chapters added</td>
                                                    </tr>
                                                ) : isRearranging ? (
                                                    rearranged.map((data, idx) => (
                                                        <tr
                                                            key={data.uuid}
                                                            draggable
                                                            onDragStart={() => setDragIndex(idx)}
                                                            onDragOver={(e) => e.preventDefault()}
                                                            onDrop={() => handleDrop(idx)}
                                                            onDragEnd={() => setDragIndex(null)}
                                                            className={dragIndex === idx ? "table-warning" : ""}
                                                            style={{ cursor: 'move' }}
                                                        >
                                                            <td className="table-user">
                                                                <i className="mdi mdi-drag-vertical" /> <strong>{idx + 1}.</strong> {data.name}
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn"
                                                                    title="Move up"
                                                                    disabled={idx === 0}
                                                                    onClick={() => moveChapter(idx, idx - 1)}
                                                                >
                                                                    <i className="mdi mdi-arrow-up-bold" />
                                                                </button>
                                                                <button
                                                                    className="btn"
                                                                    title="Move down"
                                                                    disabled={idx === rearranged.length - 1}
                                                                    onClick={() => moveChapter(idx, idx + 1)}
                                                                >
                                                                    <i className="mdi mdi-arrow-down-bold" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    slice.map((data, idx) => (
                                                        <tr key={data._id}>
                                                            <td className="table-user">
                                                                {editableRow === idx ? (
                                                                    <input
                                                                        type="text"
                                                                        id="chaptername"
                                                                        className="form-control"
                                                                        name="cname"
                                                                        placeholder="Enter Chapter Name"
                                                                        value={cname}
                                                                        onChange={(e) => setCname(e.target.value)}
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        id="chaptername"
                                                                        className="form-control"
                                                                        placeholder="Enter Chapter Name"
                                                                        value={data.name}
                                                                        disabled
                                                                    />
                                                                )}
                                                            </td>
                                                            <td>
                                                                {editableRow === idx ? (
                                                                    <button
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={() => handleUpdateChapter()}
                                                                    >
                                                                        <i className="mdi mdi-content-save-outline" /> Save
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        className="btn"
                                                                        onClick={() => {
                                                                            setEditableRow(idx);
                                                                            setId(data.uuid);
                                                                            setCname(data.name);
                                                                        }}
                                                                    >
                                                                        <i className="mdi mdi-pencil" />
                                                                    </button>
                                                                )}
                                                                <button className="btn" onClick={() => handleOnDeleteChapter(data.uuid)}>
                                                                    <i className="mdi mdi-delete" />
                                                                </button>
                                                                <button className="btn btn-warning brn-sm" onClick={() => handleOnViewTopics(data.uuid)}>
                                                                    <i className="mdi mdi-eye-circle" /> Questions
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        {isRearranging ? null : (
                                            <ReactPaginate
                                                previousLabel={"Previous"}
                                                nextLabel={"Next"}
                                                breakLabel={"..."}
                                                breakClassName={"break-me"}
                                                pageCount={pageCount}
                                                marginPagesDisplayed={2}
                                                pageRangeDisplayed={5}
                                                onPageChange={handlePageClick}
                                                containerClassName={"pagination"}
                                                subContainerClassName={"pages pagination"}
                                                activeClassName={"active"}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default ElpChapters;
